var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Service, MessageHandler } from "../../module";
import { TrainingWearableStateChangedMessage, TrainingWearableStateChangeFailedMessage, TrainingVideoStateChangedMessage, TrainingVideoPlaybackMessage, WearingPosition, convertToNumber3, ButtonType, ButtonSignal, ControllerHand, ControllerButtonMessage } from "flavr-common";
import { AttachmentChangedMessage, DropModelMessage } from "@messages/attachment/AttachmentMessage";
import { xrControllerService } from "@modules/xr/XRControllerService";
import { OnEnterContactsMessage, OnLeaveContactsMessage } from "@messages/selection";
import { camera } from "@modules/rendering/camera";
import { Vector3 } from "flavr-math";
import { ensureLocalPlayerHeadModel } from "@modules/player";
export const WearableItemStates = [
    "unworn",
    "worn",
    "removed",
    "blocked",
    "ignored"
];
export class TrainingWearableService extends Service {
    constructor(connection, trainingVrHudService) {
        super(connection);
        this.wearableItems = new Map();
        this.wearableItemToBodyMarkerId = new Map();
        this.buttonHoldLeftController = false;
        this.buttonHoldRightController = false;
        this.trainingVrHudService = trainingVrHudService;
    }
    handleControllerButtonMessage(msg) {
        if (msg.buttonType !== ButtonType.SELECT)
            return;
        const controllerData = xrControllerService.getControllerData(msg.uid);
        if (controllerData === undefined)
            return;
        const isButtonHeld = msg.input === ButtonSignal.PRESSED || msg.input === ButtonSignal.HOLD;
        const isButtonReleased = msg.input === ButtonSignal.RELEASED || msg.input === ButtonSignal.HOLD_RELEASED;
        if (!isButtonHeld && !isButtonReleased)
            return;
        const wasButtonHeld = controllerData.hand === ControllerHand.LeftHand
            ? this.buttonHoldLeftController
            : this.buttonHoldRightController;
        if (controllerData.hand === ControllerHand.LeftHand) {
            this.buttonHoldLeftController = isButtonHeld;
        }
        else if (controllerData.hand === ControllerHand.RightHand) {
            this.buttonHoldRightController = isButtonHeld;
        }
        // When the trigger is released, abort dressing on that hand.
        if (wasButtonHeld && isButtonReleased) {
            for (const item of this.wearableItems.values()) {
                if (item.processState === "dressing" && item.controllerHand === controllerData.hand) {
                    this.abortWearableItemDressingProcess(item.uid);
                }
            }
        }
    }
    handleAttachmentChangedMessage(msg) {
        const item = this.wearableItems.get(msg.childUID);
        if (item === undefined || msg.newParentUID === undefined)
            return;
        // pick up
        const controllerData = xrControllerService.getControllerData(msg.newParentUID);
        if (controllerData !== undefined)
            this.markDressable(msg.childUID, controllerData.hand);
        else
            this.abortWearableItemDressingProcess(msg.childUID);
    }
    handleOnEnterContactsMessage(msg) {
        var _a;
        const headUID = this.getHeadUID();
        for (const contact of msg.contacts) {
            const item = (_a = this.wearableItems.get(contact.collisionShapeAUID)) !== null && _a !== void 0 ? _a : this.wearableItems.get(contact.collisionShapeBUID);
            const collidesWithHead = (headUID === contact.collisionShapeAUID) || (headUID === contact.collisionShapeBUID);
            if (item === undefined || !collidesWithHead)
                continue;
            if (item.processState === "held") {
                this.startDressing(item);
            }
        }
    }
    handleOnLeaveContactsMessage(msg) {
        var _a;
        const headUID = this.getHeadUID();
        for (const contact of msg.contacts) {
            const item = (_a = this.wearableItems.get(contact.collisionShapeAUID)) !== null && _a !== void 0 ? _a : this.wearableItems.get(contact.collisionShapeBUID);
            const collidesWithHead = (headUID === contact.collisionShapeAUID) || (headUID === contact.collisionShapeBUID);
            if (item === undefined || !collidesWithHead)
                continue;
            if (item.processState === "dressing") {
                this.abortWearableItemDressingProcess(item.uid);
            }
        }
    }
    handleTrainingVideoStateChangedMessage(msg) {
        // Theoretically multiple wearable items could be associated with the same video
        Array.from(this.wearableItems.values())
            .filter(item => item.videoId === msg.trainingVideoId && msg.newState === "ended" && item.processState === "dressing")
            .forEach(item => { this.finishDressing(item); });
    }
    addClothingItem(uid, item) {
        item.processState = "none";
        this.wearableItems.set(uid, item);
        switch (item.wearingPosition) {
            case WearingPosition.Head:
                this.trainingVrHudService.createBodyMarker("head", [0, 0.3875, 0], [0.0675, 0.03125], "red");
                this.wearableItemToBodyMarkerId.set(item.name, ["head"]);
                break;
            case WearingPosition.Chin:
                this.trainingVrHudService.createBodyMarker("chin", [0, 0.3525, 0], [0.0425, 0.03125], "red");
                this.wearableItemToBodyMarkerId.set(item.name, ["chin"]);
                break;
            case WearingPosition.Torso:
                this.trainingVrHudService.createBodyMarker("torso", [0, 0.265, 0], [0.1285, 0.3], "red");
                this.wearableItemToBodyMarkerId.set(item.name, ["torso"]);
                break;
            case WearingPosition.Hands:
                this.trainingVrHudService.createBodyMarker("leftHand", [-0.1, 0.35, 0], [0.0625, 0.0625], "red");
                this.trainingVrHudService.createBodyMarker("rightHand", [0.1, 0.35, 0], [0.0625, 0.0625], "red");
                this.wearableItemToBodyMarkerId.set(item.name, ["leftHand", "rightHand"]);
                break;
            case WearingPosition.Feet:
                this.trainingVrHudService.createBodyMarker("feet", [0, 0.126, 0], [0.09, 0.0625], "red");
                this.wearableItemToBodyMarkerId.set(item.name, ["feet"]);
                break;
            case WearingPosition.Accessory:
                // Accessories may not have a specific body marker
                break;
            case WearingPosition.Eyes:
                this.trainingVrHudService.createBodyMarker("eyes", [0, 0.37, 0], [0.06, 0.03125], "red");
                this.wearableItemToBodyMarkerId.set(item.name, ["eyes"]);
                break;
        }
    }
    updateClothingItemState(uid, newState) {
        const item = this.wearableItems.get(uid);
        if (item !== undefined) {
            const previousState = item.state;
            item.state = newState;
            this.wearableItems.set(uid, item);
            this.updateMarkersForStateChange(item, previousState, newState);
            this.connection.send(new TrainingWearableStateChangedMessage(item.name, newState));
        }
    }
    getClothingItem(uid) {
        return this.wearableItems.get(uid);
    }
    removeClothingItem(uid) {
        this.wearableItems.delete(uid);
    }
    onTrainingObjectStateChanged(event) {
        const item = this.wearableItems.get(event.scenarioObject.modelUid);
        if (item === undefined)
            return;
        const previousState = item.state;
        const newState = event.nextState.name;
        item.state = newState;
        this.updateMarkersForStateChange(item, previousState, newState);
    }
    updateMarkersForStateChange(item, previousState, newState) {
        const markerIds = this.wearableItemToBodyMarkerId.get(item.name);
        if (markerIds === undefined)
            return;
        if (newState === "ignored") {
            for (const markerId of markerIds) {
                this.trainingVrHudService.setContentVisibility(markerId, false);
            }
            return;
        }
        if (previousState === "ignored") {
            for (const markerId of markerIds) {
                this.trainingVrHudService.setContentVisibility(markerId, true);
            }
        }
        const colorFor = (s) => s === "worn" ? "green" : (s === "removed" ? "gray" : "red");
        const newColor = colorFor(newState);
        const previousColor = colorFor(previousState);
        // Skip if color hasn't changed and marker was already visible
        if (previousState !== "ignored" && previousColor === newColor)
            return;
        if (newState === "worn") {
            const thumbsUpId = "thumbsUp";
            this.trainingVrHudService.setContentVisibility(thumbsUpId, true);
            setTimeout(() => {
                this.trainingVrHudService.setContentVisibility(thumbsUpId, false);
            }, 2000);
        }
        // Flash only when the color visibly changes; just set the color when revealing a hidden marker
        const flash = previousState !== "ignored";
        for (const markerId of markerIds) {
            this.trainingVrHudService.changeBodyMarkerColor(markerId, newColor, flash);
        }
    }
    /// Dressing process ///
    markDressable(uid, controllerHand) {
        const item = this.wearableItems.get(uid);
        if (item === undefined)
            return;
        item.controllerHand = controllerHand;
        item.processState = "held";
    }
    startDressing(item) {
        if (item.state === "ignored")
            return;
        if (!((item.controllerHand === ControllerHand.LeftHand && this.buttonHoldLeftController)
            || (item.controllerHand === ControllerHand.RightHand && this.buttonHoldRightController))) {
            return;
        }
        item.processState = "dressing";
        if (item.videoId === undefined) {
            this.finishDressing(item);
            return;
        }
        const playerPosition = camera.perspectiveCam.getWorldPosition(new Vector3());
        const playerDirection = camera.perspectiveCam.getWorldDirection(new Vector3());
        const videoPosition = playerPosition.clone().add(playerDirection);
        this.connection.send(new TrainingVideoPlaybackMessage(item.videoId, "play", convertToNumber3(videoPosition)));
    }
    finishDressing(item) {
        if (item.processState !== "dressing")
            return;
        if (item.state === "unworn")
            this.updateClothingItemState(item.uid, "worn");
        item.processState = "none";
        item.controllerHand = undefined;
        this.connection.send(new DropModelMessage(item.uid));
    }
    abortWearableItemDressingProcess(uid) {
        const item = this.wearableItems.get(uid);
        if (item === undefined)
            return;
        item.processState = "held";
        const messages = [];
        if (item.videoId !== undefined)
            messages.push(new TrainingVideoPlaybackMessage(item.videoId, "stop"), new TrainingVideoPlaybackMessage(item.videoId, "hide"), new TrainingVideoStateChangedMessage(item.videoId, "stop"));
        messages.push(new TrainingWearableStateChangeFailedMessage(item.name));
        this.connection.sendMultiple(messages);
    }
    /// Private helper ///
    getHeadUID() {
        return ensureLocalPlayerHeadModel().getUID();
    }
}
__decorate([
    MessageHandler(ControllerButtonMessage)
], TrainingWearableService.prototype, "handleControllerButtonMessage", null);
__decorate([
    MessageHandler(AttachmentChangedMessage)
], TrainingWearableService.prototype, "handleAttachmentChangedMessage", null);
__decorate([
    MessageHandler(OnEnterContactsMessage)
], TrainingWearableService.prototype, "handleOnEnterContactsMessage", null);
__decorate([
    MessageHandler(OnLeaveContactsMessage)
], TrainingWearableService.prototype, "handleOnLeaveContactsMessage", null);
__decorate([
    MessageHandler(TrainingVideoStateChangedMessage)
], TrainingWearableService.prototype, "handleTrainingVideoStateChangedMessage", null);
